import { ReportMetadata } from '@urc-ui/store/api/report-metadata/types';
import { REPORT_CATEGORY } from '@urc-ui/utils/types';

export interface ReportMetadataOptions {
  readonly reportCategory: string;
  readonly reportType: string;
  readonly timeUnit: string;
  readonly isCustomReport: boolean;
}

/**
 * Computes metadata options based on given report metadata, categoryId, reportTypeId.
 * Defaults to first report type in the list if reportTypeId is not provided.
 *
 * @param reportMetadata - reportMetadata that contains config for all report categories and report types
 * @param categoryId - category id such as `sp`, `sb`
 * @param reportTypeId - report type id such as `searchterm`, 'campaigns'
 */
export const getReportMetadataOptions = (
  reportMetadata: ReportMetadata,
  categoryId: string,
  reportTypeId?: string
): ReportMetadataOptions => {
  const reportCategory = reportMetadata.reportCategories[categoryId];
  let defaultReportType = reportCategory.reportTypeList[0];
  if (reportTypeId) {
    const reportType = reportCategory.reportTypeList.find(reportType => reportType.id === reportTypeId);
    if (reportType) {
      defaultReportType = reportType;
    }
  }
  return {
    reportCategory: reportCategory.id,
    reportType: defaultReportType.id,
    timeUnit: defaultReportType.timeUnits[0].id,
    isCustomReport: defaultReportType.isCustomReport ?? false
  };
};

export const getReportNameFromMetadataOptions = (reportMetadata: ReportMetadata, categoryId: string, reportTypeId: string): string => {
  const reportCategory = reportMetadata.reportCategories[categoryId];
  const reportType = reportCategory.reportTypeList.find(reportType => reportType.id === reportTypeId);
  return reportCategory.name + ' ' + reportType?.name;
};

// RMS might not have mappings for hsa and sbv program type if under unification weblab. Hence the need for fallback
export const getTaktReportTypeFromReportMetadata = (reportCategory: string, reportType: string, reportMetadata: ReportMetadata): string => {
  let coercedReportCategory = reportCategory;
  if (reportCategory === REPORT_CATEGORY.HSA) {
    coercedReportCategory = REPORT_CATEGORY.SB;
  }
  if (reportCategory === REPORT_CATEGORY.SBV && !reportMetadata.reportCategories[reportCategory]) {
    coercedReportCategory = REPORT_CATEGORY.SB;
  }
  return reportMetadata.reportCategories[coercedReportCategory].reportTypeList.find(item => item.id === reportType)
    ?.taktReportType as string;
};

export const getIsCustomReportFromReportMetadata = (
  reportCategory: string,
  reportType: string,
  reportMetadata: ReportMetadata
): boolean => {
  let coercedReportCategory = reportCategory;
  if (reportCategory === REPORT_CATEGORY.HSA) {
    coercedReportCategory = REPORT_CATEGORY.SB;
  }
  if (reportCategory === REPORT_CATEGORY.SBV && !reportMetadata.reportCategories[reportCategory]) {
    coercedReportCategory = REPORT_CATEGORY.SB;
  }
  const selectedReportConfig = reportMetadata?.reportCategories[coercedReportCategory]?.reportTypeList.find(item => item.id === reportType);
  return !!selectedReportConfig?.isCustomReport;
};

export const coerceReportCategory = (reportCategory: string): string => {
  if (reportCategory === REPORT_CATEGORY.HSA || reportCategory === REPORT_CATEGORY.SBV) {
    return REPORT_CATEGORY.SB;
  }
  return reportCategory;
};
